import React, { useContext, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  getMaxLength,
  SpecialCharacter,
  OnlyNumbers,
  AlphabetAndNumberRegex,
  convertStringToDate,
  convertStrDateFormat,
  isAgeLessThanRequired,
  UserDetails,
  FormTitles,
  FormTitleTranslationKeys,
  NavigateConst,
  domainForSegment,
  SCREEN_NAMES,
  getAnalyticsBaseConfig,
  getErrorTypeByCode,
} from 'common';
import { usePersonalInformation, useValidateGuest } from '@sentara-health/sentara-api-hooks-core';
import { Buffer } from 'buffer';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ErrorMessage,
  Button,
  Label,
  InputField,
  GlobalError,
  Headings,
  Loader,
  Paragraph,
  DatePicker
} from '@sentara-health/optimahealth_web';
import MemberInfoModal from './MemberInfoModal';
import { useForm, FormProvider } from "react-hook-form";
import { ANALYTICS_MESSAGES, DateFormatRegex, getPlanType } from 'common/utils/constants';
import { isAvMed, GlobalContext } from 'context';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';
import { setMaxDate, minYears, validateDatePickerInput } from 'common/utils';
import { setOpenReplayRegistrationTags } from 'common/openreplay/openreplay';


/** PersonInformation Component function*/
const PersonalInformation = ({
  formData,
  page,
  setFormData,
  setPage,
  plantypes,
}: any) => {
  const { t } = useTranslation();
  const { logging, ssoEndPoints } = useContext(GlobalContext);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [noContent, setNoContent] = useState<boolean>(false);
  const { PersonalInformation } = usePersonalInformation();
  const { validateGuest } = useValidateGuest();
  const [loader, setLoader] = useState<boolean>(false);
  const [memberErrorModal, setMemberErrorModal] =
    useState<boolean>(false); /** Default open value set to false*/
  const [memberErrorMessage, setMemberErrorMessage] = useState<string>('');
  const { formSubmissionSucceeded, formSubmissionFailed } = useAdobeAnalytics();
  const { pathname } = useLocation();
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: formData[page]?.firstName ?? '',
      lastName: formData[page]?.lastName ?? '',
      ssn: formData[page]?.ssn ?? '',
      dob: formData[page]?.dob ?? '',
      medicareId: formData[page]?.medicareId ?? '',
      medicaId: formData[page]?.medicaId ?? '',
      guest: formData[page]?.guest ?? '',
    },
  });
  const { handleSubmit, register, setValue, clearErrors, formState: { errors, isValid }, setError } = methods;
  const formFields: Record<string, any> = {
    Medicare: {
      id: 'medicareId',
      placeholder: t('activation.medicareIdPlaceholder'),
      label: t('activation.medicareIdLabel'),
      maxLength: getMaxLength.memberId,
      error: errors.medicareId,
      displayError: t('activation.medicareIdError'),
      restrictInputValue: AlphabetAndNumberRegex,
    },
    Medicaid: {
      id: 'medicaId',
      placeholder: t('activation.medicaIdPlaceholder'),
      maxLength: getMaxLength.memberId,
      label: t('activation.medicaIdLabel'),
      error: errors.medicaId,
      displayError: t('activation.medicaidIdError'),
      restrictInputValue: AlphabetAndNumberRegex,
    },
    individual: {
      id: 'ssn',
      placeholder: t('common.ssnPlaceHolder'),
      maxLength: getMaxLength.ssn,
      label: t('common.ssnPlaceHolder'),
      error: errors.ssn,
      displayError: t('common.ssnRequired'),
      restrictInputValue: OnlyNumbers,
    },
    guest: {
      id: 'guest',
      placeholder: t('activation.guestPlaceholder'),
      maxLength: getMaxLength.guestSSN,
      label: t('activation.guestLabel'),
      error: errors.guest,
      displayError: t('activation.guest'),
      restrictInputValue: OnlyNumbers,
    },
  };
  const { id, placeholder, maxLength, label, error, displayError, restrictInputValue } = formFields[plantypes] ?? {};
  /** validation to diplay the plantypes*/
  const planTypes = (plantypes: string) => {
    if (plantypes === t('common.getmedicaId')) {
      return getPlanType.medicaid;
    } else if (plantypes === t('common.getmedicareId')) {
      return getPlanType.medicare;
    } else {
      return getPlanType.individual;
    }
  };

  const navigate = useNavigate();

  interface InputFieldProps {
    firstName: string;
    lastName: string;
    ssn: string;
    dob: string;
    medicareId: string;
    medicaId: string;
    guest: string;

  }

  const minAgeError = () => {
    return (
      <Trans
        i18nKey="activation.lessAge"
        values={{ minYears: minYears }}
        components={{
          link1: <Link
            to={ssoEndPoints?.childOnlyPlanUrl}
            target="_blank"
            className="text-decoration-underline error"
          />
        }}
      />
    )
  };

  /** function to call the API to validate the personal information data*/
  const validateDOB = (dob: string): boolean => {
    const formattedVal = convertStringToDate(dob);
    const isMinimumAge = isAgeLessThanRequired(new Date(dob));
    if (isMinimumAge) {
      setError('dob', { type: 'lessAge' });
      return false;
    } else if (formattedVal === 'Invalid Date') {
      setError('dob', { type: 'invalidDate' });
      return false;
    }
    return true;
  };

  const handleGuestSubmission = async (data: InputFieldProps, formattedDate: string) => {
    const guestData = {
      lastName: data.lastName,
      last4SSN: data.guest.slice(-4),
      dob: formattedDate
    };
    const guestResponse = await validateGuest(guestData);

    if (guestResponse?.error) {
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['personalInfoGuestError'],
          formFailReason: `${guestResponse?.error}`
        }
      });
    } else {
      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['personalInfoSuccess']
      });
    }

    if (guestResponse?.data?.guestExists === true) {
      setErrorMessage(true);
    } else if (guestResponse?.data?.guestExists === false) {
      await setOpenReplayRegistrationTags({
        firstName: data.firstName,
        lastName: data.lastName,
        dob: data.dob,
      });
      setPage((currPage: number) => currPage + 1);
    }
    setLoader(false);
  };

  const handlePersonalInfoResponse = (response: any) => {
    if (response?.errorCode === 403 && response?.errorDetails.toLowerCase().includes('kaiser') && !isAvMed()) {
      setLoader(false);
      setMemberErrorMessage(t('activation.kaiserAlertMsg').toString() ?? '');
      setMemberErrorModal(true);
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['personalInfoError'],
          formFailReason: 'API Error: ' + response?.errorDetails?.detail,
        },
      });
      return true;
    }

    if (response?.data?.isActivated === true) {
      setErrorMessage(true);
      setLoader(false);
      const apiDetails = {
        exception: {
          errorType: getErrorTypeByCode(response?.errorCode?.toString()),
          message: 'It appears you already have an account with us',
          stack: response,
        },
        response: { httpStatus: response?.errorCode ?? 200 },
      };
      logging?.error({
        additionalDetails: getAnalyticsBaseConfig({ userId: '' }),
        apiDetails: apiDetails,
        message: ANALYTICS_MESSAGES.REG_PERSONALINFO_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
      });
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['personalInfoError'],
          formFailReason: 'It appears you already have an account with us',
        },
      });
      return true;
    }

    if (response?.error || !response?.data) {
      setNoContent(true);
      setLoader(false);
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['personalInfoError'],
          formFailReason: t('activation.formFailReason'),
        },
      });
      return true;
    }

    return false;
  };

  const logPersonalInfoError = (response: any) => {
    const apiDetails = {
      exception: {
        errorType: getErrorTypeByCode(response?.errorCode?.toString()),
        message: response?.errorDetails,
        stack: response?.error,
      },
      response: { httpStatus: response?.errorCode },
    };
    logging?.error({
      additionalDetails: getAnalyticsBaseConfig({ userId: '' }),
      apiDetails: apiDetails,
      message: ANALYTICS_MESSAGES.REG_PERSONALINFO_EXCEPTION,
      screenName: SCREEN_NAMES.REGISTRATION,
    });
  };

  const childOnlyPlanCheck = () => pathname.includes('ChildOnlyPlan');

  const onSubmit = async (data: InputFieldProps) => {
    // Skip DOB validation if navigation is activationProxy
    if (!childOnlyPlanCheck()) {
      if (!validateDOB(data.dob)) {
        return;
      }
    }


    setFormData({ ...formData, [page]: data });
    const medicare = data?.medicareId ? data?.medicareId : data?.medicaId;
    const memberId = formData[0]?.memberId ? Buffer.from(formData[0]?.memberId).toString('base64') : '';

    if (!isValid) return;

    setLoader(true);
    const dateString = convertStringToDate(data.dob);
    const formattedDate = convertStrDateFormat(dateString, true);
    const personalData = {
      firstName: data.firstName,
      lastName: data.lastName,
      ssn: data.ssn,
      dob: formattedDate,
      id: medicare ?? '',
      planType: planTypes(plantypes),
      memberId: memberId ?? "",
    };

    if (plantypes === UserDetails.guest) {
      await handleGuestSubmission(data, formattedDate);
      return;
    }

    const response: any = await PersonalInformation(personalData);
    personalData.dob = convertStringToDate(data.dob);
    setFormData({
      ...formData,
      [page]: { ...data, ...personalData },
    });

    if (response?.error) {
      logPersonalInfoError(response);
    }

    // Store registration metadata for both successful and failed verify responses
    await setOpenReplayRegistrationTags({
      firstName: data.firstName,
      lastName: data.lastName,
      dob: data.dob,
    });

    if (handlePersonalInfoResponse(response)) {
      return;
    }
    setPage((currPage: number) => currPage + 1);
    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig(),
      message: ANALYTICS_MESSAGES.REG_PERSONALINFO_SUCCESS,
      screenName: SCREEN_NAMES.REGISTRATION,
    });
    formSubmissionSucceeded({
      segment: `${domainForSegment}${pathname}`,
      form: AdobeFormConfig['personalInfoSuccess'],
    });
  };

  const closeMemberErrorModal = () => {
    setMemberErrorModal(false);
    setMemberErrorMessage('');
  };

  return (
    <FormProvider {...methods}>
      <form data-testid="submit_form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        {loader && <Loader />}
        <div className="row">
          <div className="col-lg-6 col-md-8 col-12 mt-n2" aria-live="polite">
            {errorMessage &&
              <div className='mb-1'>
                <GlobalError data-testid="memberExistId" variant="mb-4">
                  {
                    <Paragraph
                      text={
                        <>
                          {t('activation.alreadyRegister')}
                          <Link to={NavigateConst.signIn} className="px-1 d-inline-block">
                            {t('activation.sign')}
                          </Link>
                          {t('activation.alreadyRegister2')}
                          <Link to={NavigateConst.forgotUsername} className="px-1 d-inline-block">
                            {t('common.userName')}
                          </Link>
                          {t('activation.alreadyRegister3')}
                          <Link to={NavigateConst.resetPassword} className="px-1 d-inline-block">
                            {t('common.password')}
                          </Link>
                          {t('activation.alreadyRegister4')}
                        </>
                      }
                      variant="regular"
                      className="mb-0"
                    />
                  }
                </GlobalError>
              </div>
            }
            {noContent && (
              <GlobalError variant="mb-3" dataTestId="memberExistId">
                {t('common.serviceErrorRequestId')}
              </GlobalError>
            )}
            {FormTitles[page] !== '' && (
              <>
                <span className="visually-hidden me-1">
                  {t('common.registrationCurrentPage')} {[page + 1]}
                </span>
                <Headings
                  level={1}
                  text={t(FormTitleTranslationKeys[FormTitles[page]])}
                  variant="semiBold"
                  dataTestId="registrationHeading"
                  className="mb-4 pb-4"
                />
              </>
            )}
            <div className="row mb-4">
              <div className="col-12">
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId={'firstNameLabel'}
                    htmlFor={'firstName'}
                  >
                    {t('activation.firstNameLabel')}
                  </Label>
                  <div className="d-flex align-items-center">
                    <InputField
                      id={'firstName'}
                      data-testid={'firstName'}
                      maxLength={getMaxLength.name}
                      placeholder={t('activation.firstNamePlaceholder')}
                      className={`form-input ${errors.firstName ? 'error-border' : ''}`}
                      {...register('firstName', {
                        required: true,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue('firstName', e.target.value.replace(SpecialCharacter, ''));
                        }
                      })}
                    />
                  </div>
                  {errors.firstName && (
                    <ErrorMessage>{t('activation.firstNameError')}</ErrorMessage>
                  )}
                </div>
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId={'lastNameLabel'}
                    htmlFor={'lastName'}
                  >
                    {t('activation.lastNameLabel')}
                  </Label>
                  <div className="d-flex align-items-center">
                    <InputField
                      id={'lastName'}
                      data-testid={'lastName'}
                      maxLength={getMaxLength.name}
                      placeholder={t('activation.lastNamePlaceholder')}
                      className={`form-input ${errors.lastName ? 'error-border' : ''}`}
                      {...register('lastName', {
                        required: true,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue('lastName', e.target.value.replace(SpecialCharacter, ''));
                        }
                      })}
                    />
                  </div>
                  {errors.lastName && (
                    <ErrorMessage>{t('activation.lastNameError')}</ErrorMessage>
                  )}
                </div>
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="dateOfBirthInputLabel"
                    htmlFor={"dob"}
                  >
                    {t('common.dateOfBirth')}
                  </Label>
                  <DatePicker
                    selectedValue={formData[page]?.dob ?? ''}
                    maxLength={getMaxLength.dob}
                    fieldName={'dob'}
                    id={"dob"}
                    data-testid={"dob"}
                    placeholder={t('common.dateOfBirthPlaceholder')}
                    maxDate={setMaxDate(minYears)}
                    updateValue={(value: string) => {
                      setValue('dob', value, {
                        shouldValidate: true,
                      });
                      clearErrors('dob');
                    }}
                    errorClass={
                      errors?.dob ? 'error-border' : ''
                    }
                    {...register('dob', {
                      required: true,
                      validate: {
                        dateFormat: (value: string) => {
                          if (
                            DateFormatRegex.test(value) && value !== ''
                          ) {
                            return true;
                          }
                          const dateFormatError = String(
                            t(
                              'forgotUsername.dateofbirthFormat'
                            ) ?? ''
                          );
                          return dateFormatError;
                        },
                        minAge: (value: string) => {
                          const isMinAgeValid =
                            validateDatePickerInput(
                              value,
                              minYears
                            );
                          if (isMinAgeValid) {
                            return true;
                          }
                          return false
                        },
                      },
                    })}
                  />
                  {errors?.dob && (
                    <ErrorMessage>
                      {errors.dob.type === 'minAge'
                        ? minAgeError()
                        : errors.dob.message || t('common.dobRequired')}
                    </ErrorMessage>
                  )}
                </div>
                <div className="mb-3">
                  <Label
                    className="pb-1"
                    dataTestId="memberIdLabel"
                    htmlFor={id}
                  >
                    {label}
                  </Label>
                  <div className="d-flex align-items-center">
                    <InputField
                      id={id}
                      data-testid={id}
                      maxLength={maxLength}
                      placeholder={placeholder}
                      className={`${error ? 'error-border' : ''}`}
                       {...((id === 'ssn' || id === 'guest') && {
                        'data-openreplay-masked': true,
                      })}
                      {...register(id, {
                        required: true,
                        minLength: (id === 'guest' ? maxLength : undefined),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          setValue(id, e.target.value.replace(restrictInputValue, ''));
                        }
                      })}
                    />
                  </div>
                  {error && (
                    <ErrorMessage>{displayError}</ErrorMessage>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row pt-4 pb-2">
          <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
            <Button
              dataTestId={'continueButton'}
              variant="primary_btn"
              className="btn-md-width"
              disabled={!isValid}
              type="submit"
            >{t('common.continue')}</Button>
          </div>
          <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
            <Button
              dataTestId={'cancelButton'}
              variant='secondary_btn'
              className="btn-md-width"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                formSubmissionFailed({
                  segment: `${domainForSegment}${pathname}`,
                  form: AdobeFormConfig['personalInfoCancel']
                });
                if (isAvMed() || plantypes === UserDetails.guest) {
                  navigate(NavigateConst.signIn);
                } else {
                  setFormData({
                    ...formData,
                    ...{
                      [page]: formData[page]
                        ? Object.keys(formData[page]).reduce((obj, item) => {
                          obj = { ...obj, [item]: '' };
                          return obj;
                        }, {})
                        : {},
                    },
                  });
                  setPage((currPage: number) => currPage - 1);
                }
              }}
            >{t('common.cancel')}</Button>
          </div>
        </div>
        {memberErrorMessage && (
          <MemberInfoModal
            closeMemberErrorModal={closeMemberErrorModal}
            memberErrorMessage={memberErrorMessage}
            memberErrorModal={memberErrorModal}
          />
        )}
      </form>
    </FormProvider >
  );
};

export default PersonalInformation;